import { Alert, Button, EmptyState, Input, LoadingState, Modal, Textarea, useToast } from '@orbind/ui'
import { useState } from 'react'
import { PLUGIN_SCOPE_LABEL, escopoEscreve, type PluginScope } from '@orbind/core'
import {
  nomeDoPlugin,
  useAtualizarPlugin,
  useCadastrarPlugin,
  usePluginsDoConsole,
} from '../plugin/usePlugins.ts'
import { usePluginsParaRevisao, useRevisarPlugin } from '../plugin/usePortal.ts'
import '../admin/plugins.css'
import '../../pages/portal-dev.css'

/**
 * Cadastro de plugins na INSTALAÇÃO (SPEC-20260926-2036) — só o super admin.
 *
 * É o portão do marketplace: quem decide o que pode existir dentro deste
 * Orbind. Instalar num workspace é outra tela, outro dono (Administração).
 *
 * O cadastro é só a URL do manifesto — o resto o servidor busca, valida e
 * extrai. Quando o manifesto tem problema, os erros vêm campo a campo, porque
 * quem publica um plugin merece a lista inteira de uma vez.
 */

type ErroDeCampo = { campo: string; problema: string }

/** O erro da API traz `details.erros` quando a recusa foi de validação. */
function errosDoManifesto(e: unknown): ErroDeCampo[] {
  const d = (e as { details?: { erros?: unknown } } | null)?.details?.erros
  return Array.isArray(d) ? (d as ErroDeCampo[]) : []
}

export function PluginsDoConsoleSection() {
  const consulta = usePluginsDoConsole()
  const cadastrar = useCadastrarPlugin()
  const atualizar = useAtualizarPlugin()
  const revisao = usePluginsParaRevisao()
  const revisar = useRevisarPlugin()
  const toast = useToast()

  const [url, setUrl] = useState('')
  const [recusando, setRecusando] = useState<string | null>(null)
  const [motivo, setMotivo] = useState('')
  const [erros, setErros] = useState<ErroDeCampo[]>([])

  const enviar = () => {
    const alvo = url.trim()
    if (alvo === '') return
    setErros([])
    cadastrar.mutate(alvo, {
      onSuccess: (r) => {
        setUrl('')
        toast.push({
          tone: 'success',
          message: r.novo ? `Plugin ${r.key} cadastrado.` : `Plugin ${r.key} atualizado para ${r.version}.`,
        })
      },
      onError: (e) => {
        const lista = errosDoManifesto(e)
        setErros(lista)
        if (lista.length === 0) {
          toast.push({
            tone: 'danger',
            message: e instanceof Error ? e.message : 'Não foi possível cadastrar o plugin.',
          })
        }
      },
    })
  }

  return (
    <div className="plg">
      <header className="plg__head">
        <h2 className="plg__title">Plugins</h2>
        <p className="plg__sub">
          Plugins cadastrados aqui podem ser instalados pelos workspaces. Cadastrar não instala nada: só declara que
          este plugin pode existir nesta instalação. Revise os escopos que ele pede antes de liberar.
        </p>
      </header>

      <section className="plg__bloco" aria-label="Cadastrar plugin">
        <h3 className="plg__h3">Cadastrar pela URL do manifesto</h3>
        <div className="plg__form">
          <label className="plg__campo">
            <span className="plg__campo-rotulo">URL do manifesto</span>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://plugin.exemplo.com/orbind-plugin.json"
              inputMode="url"
            />
            <span className="plg__campo-dica">
              O servidor busca o manifesto, valida e extrai versão, escopos e pontos de extensão.
            </span>
          </label>
          <Button variant="primary" icon="fas fa-plus" disabled={cadastrar.isPending || url.trim() === ''} onClick={enviar}>
            {cadastrar.isPending ? 'Buscando…' : 'Cadastrar'}
          </Button>
        </div>

        {erros.length > 0 ? (
          <Alert tone="danger" title="O manifesto tem problemas">
            <ul className="plg__erros">
              {erros.map((e, i) => (
                <li key={`${e.campo}-${i}`}>
                  <code>{e.campo}</code> — {e.problema}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}
      </section>

      {/* A FILA: submetidos por terceiros, esperando decisão. Vem antes da
          lista completa porque é o que exige ação de quem abre esta tela. */}
      {(revisao.data?.plugins ?? []).filter((x) => x.status === 'submetido').length > 0 ? (
        <section className="plg__bloco" aria-label="Aguardando revisão">
          <h3 className="plg__h3">Aguardando revisão</h3>
          <ul className="plg__lista">
            {(revisao.data?.plugins ?? [])
              .filter((x) => x.status === 'submetido')
              .map((x) => (
                <li key={x.id} className="plg__card">
                  <div className="plg__card-main">
                    <span className="plg__nome">{x.name['pt-BR'] ?? x.key}</span>
                    <span className="plg__meta">
                      {x.desenvolvedor
                        ? `${x.desenvolvedor.displayName} · ${x.desenvolvedor.contactEmail}`
                        : 'sem desenvolvedor vinculado'}
                    </span>
                    <span className="plg__meta">
                      versão {x.version} · {new URL(x.baseUrl).host} · {x.extensoes}{' '}
                      {x.extensoes === 1 ? 'extensão' : 'extensões'}
                    </span>
                    {x.scopes.length > 0 ? (
                      <ul className="plg__escopos-inline">
                        {x.scopes.map((e) => (
                          <li
                            key={e}
                            className={
                              escopoEscreve(e as PluginScope) ? 'plg__chip plg__chip--escreve' : 'plg__chip'
                            }
                          >
                            {PLUGIN_SCOPE_LABEL[e as PluginScope] ?? e}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  <div className="pdv__acoes" style={{ flexDirection: 'column' }}>
                    <Button
                      variant="primary"
                      size="sm"
                      icon="fas fa-check"
                      disabled={revisar.isPending}
                      onClick={() =>
                        revisar.mutate(
                          { id: x.id, status: 'aprovado' },
                          { onSuccess: () => toast.push({ tone: 'success', message: `${x.key} aprovado.` }) },
                        )
                      }
                    >
                      Aprovar
                    </Button>
                    <Button size="sm" icon="fas fa-times" disabled={revisar.isPending} onClick={() => setRecusando(x.id)}>
                      Recusar
                    </Button>
                  </div>
                </li>
              ))}
          </ul>
        </section>
      ) : null}

      <section className="plg__bloco" aria-label="Plugins cadastrados">
        <h3 className="plg__h3">Cadastrados</h3>
        {consulta.isPending ? (
          <LoadingState rows={3} label="Carregando os plugins" />
        ) : consulta.isError ? (
          <Alert
            tone="danger"
            title="Não foi possível carregar os plugins"
            action={
              <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
                Tentar de novo
              </Button>
            }
          >
            {consulta.error instanceof Error ? consulta.error.message : 'Tente novamente em instantes.'}
          </Alert>
        ) : (consulta.data?.plugins.length ?? 0) === 0 ? (
          <EmptyState
            icon="fas fa-plug"
            title="Nenhum plugin cadastrado"
            description="Cole a URL de um manifesto acima para cadastrar o primeiro."
          />
        ) : (
          <ul className="plg__lista">
            {consulta.data?.plugins.map((p) => (
              <li key={p.id} className="plg__card">
                <div className="plg__card-main">
                  <span className="plg__nome">{nomeDoPlugin(p.name)}</span>
                  <span className="plg__meta">
                    {p.vendor?.name ? `${p.vendor.name} · ` : ''}versão {p.version} · {p.key} ·{' '}
                    {new URL(p.baseUrl).host}
                  </span>
                  <span className="plg__escopos-resumo">
                    {p.extensoes.length} {p.extensoes.length === 1 ? 'extensão' : 'extensões'} ·{' '}
                    {p.scopes.length === 0 ? 'sem escopos' : `escopos: ${p.scopes.length}`}
                  </span>
                  {p.scopes.length > 0 ? (
                    <ul className="plg__escopos-inline">
                      {p.scopes.map((s: PluginScope) => (
                        <li key={s} className={escopoEscreve(s) ? 'plg__chip plg__chip--escreve' : 'plg__chip'}>
                          {PLUGIN_SCOPE_LABEL[s] ?? s}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  icon="fas fa-sync"
                  disabled={atualizar.isPending}
                  onClick={() =>
                    atualizar.mutate(p.id, {
                      onSuccess: (r) =>
                        toast.push({ tone: 'success', message: `${r.key} atualizado para ${r.version}.` }),
                      onError: (e) =>
                        toast.push({
                          tone: 'danger',
                          message: e instanceof Error ? e.message : 'Não foi possível atualizar.',
                        }),
                    })
                  }
                >
                  Rebuscar manifesto
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        open={recusando !== null}
        onClose={() => setRecusando(null)}
        title="Recusar plugin"
        width={480}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRecusando(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={revisar.isPending || motivo.trim() === ''}
              onClick={() =>
                recusando &&
                revisar.mutate(
                  { id: recusando, status: 'recusado', motivo: motivo.trim() },
                  {
                    onSuccess: () => {
                      setRecusando(null)
                      setMotivo('')
                      toast.push({ tone: 'success', message: 'Plugin recusado.' })
                    },
                  },
                )
              }
            >
              Confirmar
            </Button>
          </>
        }
      >
        <div className="pdv__campo">
          <span className="pdv__rotulo">Por quê? *</span>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={4} />
          <span className="pdv__dica">
            O desenvolvedor vê este texto no portal dele. Diga o que precisa mudar.
          </span>
        </div>
      </Modal>
    </div>
  )
}
